<template>
  <transition name="overlay-fade">
    <div
      v-if="store.state.publicationFormVisible"
      class="pub-form-wrap"
      @click.self="hide"
    >
      <transition name="form-slide">
        <div v-if="store.state.publicationFormVisible" class="pub-form" @click.stop>
          <button v-if="formMode === 'modify'" class="delete-btn" 
            @click="$refs.alertWConfirm.show('Do you really want to delete this publication ?')">
            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
            </svg>
            Delete publication
          </button>
          <!-- Header -->
          <div class="form-header">
            <div class="header-icon">
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/></svg>
            </div>
            <div class="header-text">
              <h2 class="form-title">{{ formMode === 'modify' ? 'Edit publication' : 'New publication' }}</h2>
              <p class="form-sub">{{ formMode === 'modify' ? 'Update your audio details' : 'Share your audio with the world' }}</p>
            </div>
            <div class="header-right">
              <div class="pub-type-toggle">
                <button
                  class="type-btn"
                  :class="{ active: form.type === 'public' }"
                  @click="form.type = 'public'"
                >🌐 Public</button>
                <button
                  class="type-btn"
                  :class="{ active: form.type === 'private' }"
                  @click="form.type = 'private'"
                >🔒 Private</button>
              </div>
              <button class="close-btn" @click="hide" aria-label="Close">
                <svg style="min-width: 20px;height: 20px;" viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>
          </div>

          <div class="form-body">

            <!-- Left column -->
            <div class="col-left">

              <!-- Text -->
              <div class="field">
                <label class="field-label">Description</label>
                <textarea
                  v-model="form.text"
                  class="field-textarea"
                  placeholder="What's this audio about?"
                  rows="3"
                  maxlength="250"
                />
                <span class="char-count">{{ form.text.length }}/250</span>
              </div>

              <!-- Tags -->
              <div class="field">
                <label class="field-label">Tags</label>
                <div class="tags-input-wrap">
                  <div class="tags-list">
                    <span
                      v-for="tag in form.tags"
                      :key="tag"
                      class="tag-pill"
                    >
                      #{{ tag }}
                      <button class="tag-remove" @click="removeTag(tag)">×</button>
                    </span>
                    <input
                      v-model="tagInput"
                      class="tag-input"
                      placeholder="Add tag..."
                      @keydown.enter.prevent="addTag"
                      @keydown.,.prevent="addTag"
                      @keydown.space.prevent="addTag"
                      @blur="setTag"
                      maxlength="30"
                    />
                  </div>
                </div>
                <p class="field-hint">Enter, comma or space to add · max 10</p>
              </div>

              <!-- Audio/Video upload -->
              <div class="field">
                <!-- [CHANGE 2] Accept both audio and video files -->
                <label class="field-label">
                  Audio / Video file <span class="required">*</span>
                  <span v-if="isExtractingAudio" class="extracting-badge">⏳ Extracting audio…</span>
                </label>
                <div
                  class="dropzone"
                  :class="{ 'dz-active': dragOver.audio, 'dz-filled': form.audio }"
                  @dragover.prevent="dragOver.audio = true"
                  @dragleave="dragOver.audio = false"
                  @drop.prevent="onAudioDrop"
                  @click="$refs.audioInput.click()"
                >
                  <!-- [CHANGE 2] Accept audio/* and video/* -->
                  <input ref="audioInput" type="file" accept="audio/*,video/*" hidden @change="onAudioChange" />
                  <div v-if="!form.audio" class="dz-empty">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="26" height="26" class="dz-icon"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/></svg>
                    <p class="dz-label">Drop audio / video or tap to browse</p>
                    <p class="dz-hint">MP3 · WAV · OGG · FLAC · MP4 · MOV · WEBM</p>
                  </div>
                  <div v-else class="dz-filled-inner">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" style="color:#00e5ff;flex-shrink:0"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/></svg>
                    <div class="dz-file-info">
                      <span class="dz-filename">{{ audioFileName }}</span>
                      <span class="dz-meta">
                        {{ audioDuration }} · {{ audioSize }}
                        <span v-if="wasVideoFile" class="extracted-tag">audio extracted</span>
                      </span>
                    </div>
                    <button class="dz-clear" @click.stop="clearAudio">×</button>
                  </div>
                </div>
                <div v-if="form.audio" class="audio-preview">
                  <audio
                    ref="previewAudio"
                    :src="form.audio"
                    @loadedmetadata="onPreviewMeta"
                    controls
                    class="audio-native"
                    crossorigin="anonymous"
                  />
                </div>
              </div>

            </div>

            <!-- Right column -->
            <div class="col-right">

              <!-- Image upload -->
              <div class="field">
                <!-- [CHANGE 3] Label updated to show compression is happening -->
                <label class="field-label">
                  Cover image <span class="optional">optional</span>
                  <span v-if="isCompressingImg" class="extracting-badge">⏳ Optimizing…</span>
                </label>
                <div
                  class="img-dropzone"
                  :class="{ 'dz-active': dragOver.img }"
                  @dragover.prevent="dragOver.img = true"
                  @dragleave="dragOver.img = false"
                  @drop.prevent="onImgDrop"
                  @click="$refs.imgInput.click()"
                >
                  <input ref="imgInput" type="file" accept="image/*" hidden @change="onImgChange" />
                  <img v-if="form.img" :src="form.img" class="img-preview" alt="cover" />
                  <div v-else class="dz-empty">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22" class="dz-icon"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-1.1 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>
                    <p class="dz-label">Drop image or tap</p>
                    <p class="dz-hint">JPG · PNG · WEBP</p>
                  </div>
                  <button v-if="form.img" class="img-clear" @click.stop="clearImg">×</button>
                </div>
              </div>

              <!-- Curve color -->
              <div class="field">
                <label class="field-label">Curve color </label>
                <div class="color-grid">
                  <button
                    v-for="(pal, i) in palettes"
                    :key="i"
                    class="color-swatch"
                    :class="{ selected: form.curveColor === i }"
                    @click="form.curveColor = i"
                    :title="pal.name"
                  >
                    <span class="swatch-dots">
                      <span
                        v-for="(c, ci) in pal.colors.slice(0,3)"
                        :key="ci"
                        class="swatch-dot"
                        :style="{ background: c }"
                      />
                    </span>
                    <span class="pal-name">{{ pal.name }}</span>
                  </button>
                </div>
              </div>

            </div>
          </div>

          <!-- Footer -->
          <div class="form-footer">
            <div v-if="loading" class="upload-progress-wrap">
              <div class="upload-progress-label">
                Uploading… {{ uploadProgress }}%
              </div>
              <progress 
                class="upload-progress-bar" 
                :value="uploadProgress" 
                max="100"
              />
            </div>

            <div class="footer-left">
              <span v-if="!isValid" class="validation-hint">Audio required</span>
              <span v-else class="validation-ok">
                <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                Ready to publish
              </span>
            </div>
            <div class="footer-actions">
              <button v-if="formMode !== 'modify'" class="btn-secondary" @click="resetForm">Reset</button>
              <button class="btn-primary" :disabled="!isValid || isExtractingAudio || isCompressingImg" @click="onSubmit">
                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
                {{ formMode === 'modify' ? 'Save changes' : 'Publish' }}
              </button>
            </div>
          </div>

        </div>
      </transition>
    </div>
  </transition>
  <Loader :loading="loading" />
     <AlertW type="warning" :showCancel="true" @confirm="onDelete" ref="alertWConfirm" />
     <AlertW type="success" ref="alertW" @confirm="hide"/>

</template>

<script setup>
/* eslint-disable no-undef */
import { ref, reactive, computed, onMounted, nextTick, watch, inject } from 'vue'
import AlertW from '@/views/components/alertW/AlertW.vue'
import Loader from "@/views/components/loader/Loader.vue"
import { useStore } from 'vuex'
// [CHANGE 3] Import browser-image-compression for optimized image processing
// Install with: npm install browser-image-compression
import imageCompression from 'browser-image-compression'
import { Mp3Encoder } from '@breezystack/lamejs'

const store = useStore()
const axios = inject("axios")

// ─── Visibility ───────────────────────────────────────────────────────────────
const loading = ref(false)
const alertW = ref(null)

function show(type = 'create', infos = null) {
  formMode.value = type
  store.commit('setPublicationFormVisibility',true)

  if (type === 'modify' && infos) {
    editId.value       = infos._id
    pubType.value      = infos.type
    form.text          = infos.text        || ''
    form.type          = infos.type       || 'public'
    form.curveType     = infos.curveType   ?? 0
    form.curveColor    = infos.curveColor  ?? 0
    form.tags          = infos.tags        || []
    if (infos.imgMini) form.img   = store.state.baseUrl + 'images/' + infos.imgMini
    if (infos.audio) form.audio = store.state.baseUrl + 'audios/' + infos.audio
  }

  nextTick(redrawAllPreviews)
}

function hide() {
  store.commit('setPublicationFormVisibility',false)
}

// ─── Form state ───────────────────────────────────────────────────────────────
const form = reactive({
  userId:     '',
  text:       '',
  img:        null,
  imgMini:    null,
  audio:      null,
  duration:   0,
  curveType:  0,
  curveColor: 0,
  type:       'public',
  stats:      { shares: 0, views: 0, likes: 0, comments: 0 },
  tags:       [],
  createdAt:  null,
  deletedAt:  null,
})

const tagInput         = ref('')
const dragOver         = reactive({ audio: false, img: false })
const audioFileName    = ref('')
const audioSize        = ref('')
const audioDuration    = ref('0:00')
const previewAudio     = ref(null)
const audioInput       = ref(null)
const imgInput         = ref(null)
const formMode         = ref('create') // 'create' | 'modify'
const editId           = ref(null)
const pubType          = ref('')

// [CHANGE 2] Track extracted audio File to send to backend, and UI states
const extractedAudioFile = ref(null)   // the final audio File (from audio or extracted from video)
const wasVideoFile       = ref(false)  // whether source was a video
const isExtractingAudio  = ref(false)

// [CHANGE 3] Track compressed image File and compression state
const compressedImageFile = ref(null)
const isCompressingImg    = ref(false)
const uploadProgress = ref(0)

// ─── Palettes ─────────────────────────────────────────────────────────────────
const palettes = [
  { name: 'Cyan',     colors: ['#00e5ff', '#00b4d8'] },
  { name: 'Sunset',   colors: ['#ff6b6b', '#ffd93d'] },
  { name: 'Aurora',   colors: ['#a29bfe', '#fd79a8'] },
  { name: 'Forest',   colors: ['#00b894', '#55efc4'] },
  { name: 'Fire',     colors: ['#fdcb6e', '#e17055'] },
  { name: 'Neon',     colors: ['#6c5ce7', '#a29bfe', '#fd79a8'] },
  { name: 'Ocean',    colors: ['#00cec9', '#0984e3', '#6c5ce7'] },
  { name: 'Spectrum', colors: ['#55efc4', '#fdcb6e', '#e17055', '#d63031'] },
]

// ─── Curves ───────────────────────────────────────────────────────────────────
const curves = [
  { name: 'Waveform' },
  { name: 'Mirror' },
  { name: 'EQ Bars' },
  { name: 'Heatmap' },
  { name: 'FFT' },
  { name: 'Oscillogram' },
]

// ─── Canvas refs ──────────────────────────────────────────────────────────────
const curveCanvases = ref([])

function drawCurvePreview(canvas, curveIndex, paletteIndex) {
  if (!canvas) return
  const ctx = canvas.getContext('2d')
  const W   = canvas.width
  const H   = canvas.height
  ctx.clearRect(0, 0, W, H)

  const pal = palettes[paletteIndex ?? form.curveColor].colors
  const col = pal[0]

  const pts      = 24
  const fakeFreq = Array.from({ length: pts }, (_, i) =>
    0.2 + 0.65 * Math.abs(Math.sin(i * 0.7 + 1.2)) * Math.abs(Math.cos(i * 0.3))
  )
  const fakeTime = Array.from({ length: pts }, (_, i) =>
    0.5 * Math.sin(i * 0.55) + 0.25 * Math.sin(i * 1.3 + 0.8)
  )

  function lerp(a, b, t) { return a + (b - a) * t }
  function palCol(t) {
    const scaled = Math.max(0, Math.min(1, t)) * (pal.length - 1)
    const lo = Math.floor(scaled)
    const hi = Math.min(lo + 1, pal.length - 1)
    const f  = scaled - lo
    const parse = h => { const n = parseInt(h.replace('#',''), 16); return [(n>>16)&255,(n>>8)&255,n&255] }
    const [r0,g0] = parse(pal[lo])
    const [r1,g1,b1] = parse(pal[hi])
    return `rgb(${Math.round(lerp(r0,r1,f))},${Math.round(lerp(g0,g1,f))},${Math.round(lerp(g0,b1,f))})`
  }

  switch (curveIndex) {
    case 0: {
      const grad = ctx.createLinearGradient(0,0,W,0)
      pal.forEach((c,i) => grad.addColorStop(i/(pal.length-1||1), c))
      ctx.beginPath(); ctx.strokeStyle = grad; ctx.lineWidth = 2
      fakeTime.forEach((v,i) => { const x=(i/(pts-1))*W, y=H/2-v*H*0.38; i===0?ctx.moveTo(x,y):ctx.lineTo(x,y) })
      ctx.stroke(); break
    }
    case 1: {
      const bw = W/pts
      fakeFreq.forEach((v,i) => {
        const x=i*bw, h=Math.max(1,v*(H/2)*0.9)
        ctx.fillStyle=palCol(i/(pts-1)); ctx.fillRect(x+0.5,H/2-h,bw-1,h)
        ctx.globalAlpha=0.25; ctx.fillRect(x+0.5,H/2,bw-1,h); ctx.globalAlpha=1
      }); break
    }
    case 2: {
      const bw = W/pts
      fakeFreq.forEach((v,i) => {
        const x=i*bw, h=Math.max(2,v*H*0.85)
        ctx.fillStyle=palCol(i/(pts-1)); ctx.fillRect(x+0.5,H-h,bw-1,h)
      }); break
    }
    case 3: {
      const bands=6, bh=H/bands
      for (let b=0;b<bands;b++) {
        const v=fakeFreq[b*4]||0.5, fw=Math.max(2,v*W), c=palCol(b/(bands-1))
        const g=ctx.createLinearGradient(0,0,fw,0); g.addColorStop(0,c); g.addColorStop(1,'transparent')
        ctx.fillStyle=g; ctx.fillRect(0,b*bh+0.5,fw,bh-1)
      } break
    }
    case 4: {
      const bw=W/pts, my=H/2
      fakeFreq.forEach((v,i) => {
        const x=i*bw, h=Math.max(2,v*my*1.7)
        ctx.fillStyle=palCol(i/(pts-1)); ctx.fillRect(x+0.5,my-h/2,bw-1,h)
      }); break
    }
    case 5: {
      ctx.strokeStyle='rgba(0,255,80,0.12)'; ctx.lineWidth=0.5
      for(let c=0;c<=4;c++){const x=(c/4)*W;ctx.beginPath();ctx.moveTo(x,0);ctx.lineTo(x,H);ctx.stroke()}
      for(let r=0;r<=2;r++){const y=(r/2)*H;ctx.beginPath();ctx.moveTo(0,y);ctx.lineTo(W,y);ctx.stroke()}
      ctx.beginPath(); ctx.strokeStyle=col; ctx.lineWidth=1.5
      fakeTime.forEach((v,i)=>{const x=(i/(pts-1))*W,y=H/2-v*H*0.38;i===0?ctx.moveTo(x,y):ctx.lineTo(x,y)})
      ctx.stroke(); break
    }
  }
}

function redrawAllPreviews() {
  nextTick(() => {
    curves.forEach((_, i) => {
      const canvas = curveCanvases.value[i]
      if (canvas) drawCurvePreview(canvas, i, form.curveColor)
    })
  })
}

// ─── Tags ─────────────────────────────────────────────────────────────────────
function addTag() {
  const val = tagInput.value.trim().replace(/^#/, '').replace(/[,\s]/g, '')
  if (val && !form.tags.includes(val) && form.tags.length < 10) form.tags.push(val)
  tagInput.value = ''
}
function setTag() {
  const val = tagInput.value.split(/[,\s]+/).filter(Boolean);
  if(val) form.tags = [...form.tags, ...val].slice(0,10)
  tagInput.value = ''
}
function removeTag(tag) { form.tags = form.tags.filter(t => t !== tag) }

// ─── Audio ────────────────────────────────────────────────────────────────────
function onAudioChange(e) { const f = e.target.files[0]; if (f) processMediaFile(f) }
function onAudioDrop(e) {
  dragOver.audio = false
  const f = e.dataTransfer.files[0]
  // [CHANGE 2] Accept video files in drop too
  if (f && (f.type.startsWith('audio/') || f.type.startsWith('video/'))) processMediaFile(f)
}

// [CHANGE 2] Unified handler for both audio and video files
async function processMediaFile(file) {
  clearAudio()

  // if (file.type.startsWith('video/')) {
  const c =false
  if (c) { // never
    // 300MB max for video
    if (file.size > 300 * 1024 * 1024) {
      alert('Video must be under 300MB.')
      if (audioInput.value) audioInput.value.value = ''
      return
    }

    wasVideoFile.value = false

    // Check if browser supports in-browser extraction
    const canExtract = !!(window.AudioContext || window.webkitAudioContext)

    if (canExtract) {
      isExtractingAudio.value = true
      try {
        const audioFile          = await extractAudioFromVideo(file)
        extractedAudioFile.value = audioFile
        form.audio               = URL.createObjectURL(audioFile)
        audioFileName.value      = audioFile.name
        audioSize.value          = formatBytes(audioFile.size)
        wasVideoFile.value = true
      } catch (err) {
        // AudioContext failed (bad format etc.) → fall back to raw video
        console.warn('In-browser extraction failed, sending raw video:', err)
        extractedAudioFile.value = file
        form.audio               = URL.createObjectURL(file)
        audioFileName.value      = file.name + ' (upload audio extraction)'
        audioSize.value          = formatBytes(file.size)
      } finally {
        isExtractingAudio.value = false
      }
    } else {
      // Old browser → send raw video to backend
      extractedAudioFile.value = file
      form.audio               = URL.createObjectURL(file)
      audioFileName.value      = file.name + ' (upload audio extraction)'
      audioSize.value          = formatBytes(file.size)
    }

  } else {
    wasVideoFile.value       = false
    extractedAudioFile.value = file
    form.audio               = URL.createObjectURL(file)
    audioFileName.value      = file.name
    audioSize.value          = formatBytes(file.size)
  }
}

/**
 * [CHANGE 2] Extract audio from a video File using the Web Audio API.
 * Decodes the video in an <video> element, routes its audio into an
 * AudioContext, records it with MediaRecorder, and returns a .webm Blob
 * wrapped in a File object.
 */
async function extractAudioFromVideo(videoFile) {

  const AudioContext = window.AudioContext || window.webkitAudioContext
  const ctx          = new AudioContext()
  const arrayBuffer  = await videoFile.arrayBuffer()

  // Use MP4Box to demux (300KB) OR use decodeAudioData as a simpler path
  // For broad format support, decodeAudioData handles the container parsing
  try {
    const audioBuffer  = await ctx.decodeAudioData(arrayBuffer)
    const mp3Blob  = audioBufferToMp3(audioBuffer)
    const baseName     = videoFile.name.replace(/\.[^.]+$/, '')
    await ctx.close()
    return new File([mp3Blob], `${baseName}.mp3`, { type: 'audio/mp3' })
  } catch(e) {
    await ctx.close()
    throw new Error('Could not extract audio: ' + e.message)
  }
}

function audioBufferToMp3(audioBuffer) {
  const numChannels = audioBuffer.numberOfChannels
  const sampleRate  = audioBuffer.sampleRate
  //const mp3encoder  = new lamejs.Mp3Encoder(numChannels, sampleRate, 128) // 128kbps
  const mp3encoder = new Mp3Encoder(numChannels, sampleRate, 128)
  const blockSize   = 1152
  const mp3Data     = []

  const left  = float32ToInt16(audioBuffer.getChannelData(0))
  const right  = numChannels > 1 ? float32ToInt16(audioBuffer.getChannelData(1)) : null
  const max1 = left.length
  for (let i = 0; i < max1; i += blockSize) {
    const leftChunk  = left.subarray(i, i + blockSize)
    const rightChunk = right ? right.subarray(i, i + blockSize) : null
    const encoded    = right
      ? mp3encoder.encodeBuffer(leftChunk, rightChunk)
      : mp3encoder.encodeBuffer(leftChunk)
    if (encoded.length > 0) mp3Data.push(encoded)
  }

  const flushed = mp3encoder.flush()
  if (flushed.length > 0) mp3Data.push(flushed)

  return new Blob(mp3Data, { type: 'audio/mp3' })
}

function float32ToInt16(float32Array) {
  const int16 = new Int16Array(float32Array.length)
  const max = float32Array.length
  for (let i = 0; i < max; i++) {
    const s = Math.max(-1, Math.min(1, float32Array[i]))
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF
  }
  return int16
}

function clearAudio() {
  if (form.audio) URL.revokeObjectURL(form.audio)
  form.audio           = null
  form.duration        = 0
  audioFileName.value  = ''
  audioSize.value      = ''
  audioDuration.value  = '0:00'
  wasVideoFile.value   = false
  extractedAudioFile.value = null
  if (audioInput.value) audioInput.value.value = ''
}
function onPreviewMeta(e) {
  const dur = e.target.duration
  form.duration       = Math.round(dur)
  audioDuration.value = formatTime(dur)
}

// ─── Image ────────────────────────────────────────────────────────────────────
function onImgChange(e) { const f = e.target.files[0]; if (f) processImgFile(f) }
function onImgDrop(e) {
  dragOver.img = false
  const f = e.dataTransfer.files[0]
  if (f && f.type.startsWith('image/')) processImgFile(f)
}

// [CHANGE 3] Use browser-image-compression to compress image before any use
async function processImgFile(file) {
  isCompressingImg.value = true
  compressedImageFile.value = null

  try {
    // Compression options: max 1MB, max 1920px, good quality
    const options = {
      maxSizeMB:           1,
      maxWidthOrHeight:    800,
      useWebWorker:        true,
      fileType:            'image/jpeg',
      initialQuality:      0.82,
    }
    const compressed = await imageCompression(file, options)

    // Store the compressed File for backend upload
    // imageCompression returns a Blob; wrap in File to preserve name
    compressedImageFile.value = new File(
      [compressed],
      file.name.replace(/\.[^.]+$/, '') + '.jpg',
      { type: 'image/jpeg' }
    )

    // Show preview from compressed blob
    const previewURL = URL.createObjectURL(compressedImageFile.value)
    form.img = previewURL

    // Generate 80×80 thumbnail (imgMini) from the compressed image
    const img = new Image()
    img.onload = () => {
      const c   = document.createElement('canvas')
      c.width   = 80
      c.height  = 80
      const ctx = c.getContext('2d')
      const aspect = img.width / img.height
      let sx=0, sy=0, sw=img.width, sh=img.height
      if (aspect > 1) { sw = img.height; sx = (img.width - sw) / 2 }
      else            { sh = img.width;  sy = (img.height - sh) / 2 }
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, 80, 80)
      form.imgMini = c.toDataURL('image/jpeg', 0.8)
      URL.revokeObjectURL(previewURL)
      // Re-set form.img to the canvas-free data URL for display
      // (keep object URL for display is fine; revoke when cleared)
      form.img = URL.createObjectURL(compressedImageFile.value)
    }
    img.src = previewURL
  } catch (err) {
    console.error('Image compression failed, using original:', err)
    // Fallback: use original file
    compressedImageFile.value = file
    const reader = new FileReader()
    reader.onload = ev => { form.img = ev.target.result }
    reader.readAsDataURL(file)
  } finally {
    isCompressingImg.value = false
  }
}

function clearImg() {
  form.img              = null
  form.imgMini          = null
  compressedImageFile.value = null
  if (imgInput.value) imgInput.value.value = ''
}

// ─── Validation & Submit ──────────────────────────────────────────────────────
const isValid = computed(() => !!form.audio)

async function onSubmit() {
  if (!isValid.value) return
  loading.value = true

  // [CHANGE 2] Use extractedAudioFile (audio-only) instead of raw input file
  // [CHANGE 3] Use compressedImageFile instead of raw image input file
  const audioFile = extractedAudioFile.value || audioInput.value?.files[0]
  const imageFile = compressedImageFile.value || imgInput.value?.files[0]

  const formData = new FormData()

  if (formMode.value === 'modify') {
    formData.append("type",          "modify")
    formData.append("publicationId", editId.value)
    formData.append("text",          form.text)
    formData.append("type1",         form.type)
    formData.append("curveType",     form.curveType)
    formData.append("curveColor",    form.curveColor)
    formData.append("tags",          JSON.stringify(form.tags))
    if (imageFile) formData.append("image", imageFile)
    if (audioFile) formData.append("audio", audioFile)
  } else {
    formData.append("type",       "create")
    formData.append("text",       form.text)
    formData.append("type1",      form.type)
    formData.append("curveType",  form.curveType)
    formData.append("curveColor", form.curveColor)
    formData.append("image",      imageFile)
    formData.append("audio",      audioFile)
    formData.append("tags",       JSON.stringify(form.tags))
  }

  try {
    const res = await axios.post('/publication.php', formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (progressEvent) => {
        uploadProgress.value = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        )
      }
    })
    store.commit('setModifiedPublication', res.data)
    alertW.value.show(
      formMode.value === 'modify' ? 'Publication updated' : 'Publication successfully created',
      'Success'
    )
    resetForm()
  } catch (e) {
    console.log(e.response?.data?.error || e.message)
  } finally {
    loading.value = false
  }
}

async function onDelete() {
  loading.value = true
  try {
      const res = await axios.post('/publication.php',{type:"delete",publicationId:editId.value })
      store.commit('setModifiedPublication', {_id:editId.value, type:pubType.value, type1:'delete'})
      resetForm()
      hide()
      console.log(res.data)
  } catch (e) {
    console.log(e.response)
  }  finally {
    loading.value = false
  }
}

function resetForm() {
  clearAudio(); clearImg()
  form.text = ''; form.tags = []; form.curveType = 0
  form.curveColor = 0; form.type = 'public'; form.duration = 0
  tagInput.value = ''
  formMode.value = 'create'
  editId.value   = null
  uploadProgress.value = 0
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatBytes(b) {
  if (b < 1024)    return b + ' B'
  if (b < 1048576) return (b/1024).toFixed(1) + ' KB'
  return (b/1048576).toFixed(1) + ' MB'
}
function formatTime(s = 0) {
  return `${Math.floor(s/60)}:${Math.floor(s%60).toString().padStart(2,'0')}`
}

// ─── Expose public API ────────────────────────────────────────────────────────
defineExpose({ show, hide })

watch(() => form.curveColor, redrawAllPreviews)
onMounted(() => redrawAllPreviews())
</script>

<style scoped>
/* ─── Overlay ──────────────────────────────────────── */
.pub-form-wrap {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 3000;
  max-height: 100%;
  height: 100vh;
  background: rgba(5, 8, 28, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  overflow-y: auto;
  font-family: 'Outfit', sans-serif;
}

/* ─── Form card ────────────────────────────────────── */
.pub-form {
  width: 100%;
  max-height: 100%;
  max-width: 860px;
  background: #0f1535;
  border-radius: 20px;
  border: 1px solid rgba(255,255,255,0.08);
  overflow: auto;
  min-width: 0;
}

/* ─── Header ───────────────────────────────────────── */
.form-header {
  display: flex;
  align-items: center;
  padding: 18px 22px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  background: rgba(255,255,255,0.02);
}
.form-header > * + * { margin-left: 14px; }

.header-icon {
  width: 42px;
  height: 42px;
  border-radius: 11px;
  background: rgba(0,229,255,0.1);
  border: 1px solid rgba(0,229,255,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00e5ff;
  flex-shrink: 0;
}
.header-icon svg { width: 22px; height: 22px; }

.header-text { flex: 1; min-width: 0; }

.form-title {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.form-sub {
  font-size: 12px;
  color: rgba(255,255,255,0.35);
  margin: 2px 0 0;
}

.header-right {
  display: flex;
  align-items: center;
  margin-left: auto;
  flex-shrink: 0;
}
.header-right > * + * { margin-left: 10px; }

.delete-btn {
  display: flex;
  align-items: center;
  background: rgba(255, 77, 109, 0.12);
  border: 1px solid rgba(255, 77, 109, 0.38);
  border-radius: 10px;
  color: #ff4d6d;
  font-family: 'Outfit', sans-serif;
  font-size: 13px;
  font-weight: 700;
  padding: 7px 14px;
  cursor: pointer;
  margin: 10px;
  transition: background 0.15s, border-color 0.15s;
}
.delete-btn svg { margin-right: 6px; width: 15px; height: 15px; }
.delete-btn:hover { background: rgba(255, 77, 109, 0.25); border-color: rgba(255, 77, 109, 0.6); }

.pub-type-toggle {
  display: flex;
  background: rgba(255,255,255,0.05);
  border-radius: 10px;
  padding: 3px;
}
.pub-type-toggle > * + * { margin-left: 2px; }

.type-btn {
  background: none;
  border: none;
  color: rgba(255,255,255,0.38);
  font-family: 'Outfit', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
  white-space: nowrap;
}
.type-btn.active {
  background: rgba(0,229,255,0.12);
  color: #00e5ff;
  border: 1px solid rgba(0,229,255,0.3);
}

.close-btn {
  width: 34px;
  height: 34px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 9px;
  color: rgba(255,255,255,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.close-btn svg { width: 18px; height: 18px; }
.close-btn:hover { background: rgba(255,77,109,0.18); color: #ff4d6d; border-color: rgba(255,77,109,0.3); }

/* ─── Body ─────────────────────────────────────────── */
.form-body {
  display: flex;
  flex-wrap: wrap;
}

.col-left {
  width: 50%;
  box-sizing: border-box;
  padding: 22px;
  border-right: 1px solid rgba(255,255,255,0.06);
  display: flex;
  flex-direction: column;
}
.col-left > * + * { margin-top: 20px; }

.col-right {
  width: 50%;
  box-sizing: border-box;
  padding: 22px;
  display: flex;
  flex-direction: column;
}
.col-right > * + * { margin-top: 20px; }

/* ─── Fields ───────────────────────────────────────── */
.field {
  display: flex;
  flex-direction: column;
}
.field > * + * { margin-top: 8px; }

.field-label {
  font-size: 12px;
  font-weight: 700;
  color: rgba(255,255,255,0.45);
  letter-spacing: 0.7px;
  text-transform: uppercase;
  display: flex;
  align-items: center;
}
.field-label > * + * { margin-left: 7px; }
.required { color: #ff4d6d; }
.optional {
  font-size: 11px;
  color: rgba(255,255,255,0.28);
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
}

.extracting-badge {
  font-size: 11px;
  color: #fdcb6e;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  background: rgba(253,203,110,0.1);
  border: 1px solid rgba(253,203,110,0.3);
  border-radius: 6px;
  padding: 2px 8px;
}

.field-textarea {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 11px;
  color: #fff;
  font-family: 'Outfit', sans-serif;
  font-size: 14px;
  padding: 11px 13px;
  resize: vertical;
  min-height: 70px;
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
  box-sizing: border-box;
}
.field-textarea::placeholder { color: rgba(255,255,255,0.18); }
.field-textarea:focus { border-color: rgba(0,229,255,0.4); }

.char-count {
  font-size: 11px;
  color: rgba(255,255,255,0.28);
  font-family: 'Space Mono', monospace;
  text-align: right;
  margin-top: -3px;
}
.field-hint { font-size: 11px; color: rgba(255,255,255,0.22); margin: 0; }

/* ─── Tags ─────────────────────────────────────────── */
.tags-input-wrap {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 11px;
  padding: 8px 10px;
  cursor: text;
  transition: border-color 0.2s;
}
.tags-input-wrap:focus-within { border-color: rgba(0,229,255,0.4); }

.tags-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}
.tags-list > * { margin: 3px; }

.tag-pill {
  display: inline-flex;
  align-items: center;
  background: rgba(0,229,255,0.1);
  border: 1px solid rgba(0,229,255,0.25);
  border-radius: 99px;
  color: #00e5ff;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
}
.tag-pill > * + * { margin-left: 5px; }

.tag-remove {
  background: none;
  border: none;
  color: rgba(0,229,255,0.55);
  cursor: pointer;
  font-size: 15px;
  line-height: 1;
  padding: 0;
  transition: color 0.15s;
}
.tag-remove:hover { color: #ff4d6d; }

.tag-input {
  background: none;
  border: none;
  outline: none;
  color: #fff;
  font-family: 'Outfit', sans-serif;
  font-size: 13px;
  flex: 1;
  min-width: 60px;
}
.tag-input::placeholder { color: rgba(255,255,255,0.18); }

/* ─── Audio Dropzone ───────────────────────────────── */
.dropzone {
  background: rgba(255,255,255,0.03);
  border: 1.5px dashed rgba(255,255,255,0.11);
  border-radius: 13px;
  padding: 18px 14px;
  cursor: pointer;
  min-height: 88px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s, background 0.2s;
}
.dropzone:hover, .dropzone.dz-active {
  border-color: rgba(0,229,255,0.45);
  background: rgba(0,229,255,0.04);
}
.dropzone.dz-filled {
  border-style: solid;
  border-color: rgba(0,229,255,0.3);
  background: rgba(0,229,255,0.05);
}

.dz-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.dz-empty > * + * { margin-top: 6px; }
.dz-icon { color: rgba(255,255,255,0.18); }
.dz-icon svg { width: 28px; height: 28px; }
.dz-label { font-size: 13px; color: rgba(255,255,255,0.4); margin: 0; }
.dz-hint  { font-size: 11px; color: rgba(255,255,255,0.18); margin: 0; }

.dz-filled-inner {
  display: flex;
  align-items: center;
  width: 100%;
}
.dz-filled-inner > * + * { margin-left: 12px; }
.dz-filled-inner > svg { width: 20px; height: 20px; }

.dz-file-info {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}
.dz-filename {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dz-meta {
  font-size: 11px;
  color: rgba(255,255,255,0.32);
  font-family: 'Space Mono', monospace;
  display: flex;
  align-items: center;
}
.dz-meta > * + * { margin-left: 7px; }

.extracted-tag {
  font-size: 10px;
  color: #a29bfe;
  background: rgba(162,155,254,0.12);
  border: 1px solid rgba(162,155,254,0.3);
  border-radius: 4px;
  padding: 1px 6px;
  font-family: 'Outfit', sans-serif;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  white-space: nowrap;
}

.dz-clear {
  background: rgba(255,255,255,0.07);
  border: none;
  color: rgba(255,255,255,0.45);
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
}
.dz-clear:hover { background: rgba(255,77,109,0.2); color: #ff4d6d; }

.audio-preview { margin-top: 9px; }
.audio-native {
  width: 100%;
  height: 36px;
  border-radius: 9px;
  accent-color: #00e5ff;
  outline: none;
}

/* ─── Image Dropzone ───────────────────────────────── */
.img-dropzone {
  position: relative;
  background: rgba(255,255,255,0.03);
  border: 1.5px dashed rgba(255,255,255,0.11);
  border-radius: 13px;
  height: 124px;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s, background 0.2s;
}
.img-dropzone:hover, .img-dropzone.dz-active {
  border-color: rgba(162,155,254,0.45);
  background: rgba(162,155,254,0.05);
}
.img-preview { width: 100%; height: 100%; object-fit: cover; }
.img-clear {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0,0,0,0.6);
  border: none;
  color: #fff;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.img-clear:hover { background: rgba(255,77,109,0.7); }

/* ─── Color Grid ───────────────────────────────────── */
.color-grid {
  display: flex;
  flex-wrap: wrap;
}
.color-swatch {
  width: calc(25% - 6px);
  box-sizing: border-box;
  margin: 3px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  padding: 8px 4px 6px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: border-color 0.18s, background 0.18s;
}
.color-swatch > * + * { margin-top: 5px; }
.color-swatch:hover { border-color: rgba(255,255,255,0.18); background: rgba(255,255,255,0.07); }
.color-swatch.selected { border-color: rgba(255,255,255,0.45); background: rgba(255,255,255,0.09); }

.swatch-dots { display: flex; }
.swatch-dots > * + * { margin-left: 2px; }
.swatch-dot { width: 10px; height: 10px; border-radius: 50%; }
.pal-name {
  font-size: 10px;
  font-weight: 600;
  color: rgba(255,255,255,0.3);
  white-space: nowrap;
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
}
.color-swatch.selected .pal-name { color: rgba(255,255,255,0.65); }

/* ─── Footer ───────────────────────────────────────── */
.form-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 16px 22px;
  border-top: 1px solid rgba(255,255,255,0.06);
  background: rgba(255,255,255,0.01);
}
.form-footer > * + * { margin-top: 10px; }

.footer-actions { display: flex; }
.footer-actions > * + * { margin-left: 10px; }

.validation-hint {
  font-size: 12px;
  color: rgba(255,100,100,0.65);
  display: flex;
  align-items: center;
}
.validation-ok {
  font-size: 12px;
  color: rgba(0,229,255,0.65);
  display: flex;
  align-items: center;
}
.validation-hint > * + *,
.validation-ok   > * + * { margin-left: 5px; }
.validation-ok svg,
.validation-hint svg { width: 14px; height: 14px; }

.btn-secondary {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.11);
  border-radius: 10px;
  color: rgba(255,255,255,0.55);
  font-family: 'Outfit', sans-serif;
  font-size: 13px;
  font-weight: 600;
  padding: 9px 18px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.btn-secondary:hover { background: rgba(255,255,255,0.1); color: #fff; }

.btn-primary {
  background: rgba(0,229,255,0.12);
  border: 1px solid rgba(0,229,255,0.38);
  border-radius: 10px;
  color: #00e5ff;
  font-family: 'Outfit', sans-serif;
  font-size: 13px;
  font-weight: 700;
  padding: 9px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background 0.15s, border-color 0.15s;
}
.btn-primary svg { margin-right: 7px; width: 15px; height: 15px; }
.btn-primary:hover:not(:disabled) { background: rgba(0,229,255,0.22); border-color: rgba(0,229,255,0.6); }
.btn-primary:disabled { opacity: 0.3; cursor: not-allowed; }

.upload-progress-wrap {
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
}
.upload-progress-wrap > * + * {
  margin-top: 5px;
}
.upload-progress-label {
  font-size: 11px;
  color: rgba(255,255,255,0.45);
  font-family: 'Space Mono', monospace;
}
.upload-progress-bar {
  width: 100%;
  height: 6px;
  border-radius: 99px;
  overflow: hidden;
  border: none;
  appearance: none;
  background: rgba(255,255,255,0.08);
}
.upload-progress-bar::-webkit-progress-bar {
  background: rgba(255,255,255,0.08);
  border-radius: 99px;
}
.upload-progress-bar::-webkit-progress-value {
  background: #00e5ff;
  border-radius: 99px;
  transition: width 0.2s ease;
}
.upload-progress-bar::-moz-progress-bar {
  background: #00e5ff;
  border-radius: 99px;
}

/* ─── Responsive ≤ 600px ───────────────────────────── */
@media (max-width: 600px) {
  .pub-form-wrap { max-width: 100%; padding: 0; display: block; }
  .pub-form { width: 100%; height: 100%; border-radius: 0; overflow-y: auto; }
  .col-left, .col-right { width: 100%; box-sizing: border-box; }
  .col-left  { border-right: none; border-bottom: 1px solid rgba(255,255,255,0.06); padding: 16px; }
  .col-left > * + * { margin-top: 14px; }
  .col-right { padding: 16px; }
  .col-right > * + * { margin-top: 14px; }
  .form-header { padding: 13px 16px; }
  .form-header > * + * { margin-left: 10px; }
  .form-title  { font-size: 15px; }
  .form-sub    { display: none; }
  .header-right > * + * { margin-left: 6px; }
  .img-dropzone { height: 90px; }
  .form-footer  { padding: 12px 16px; }
}

/* ════════════════════════════════════════════════════
   Small-screen override — restore original proportions
   ════════════════════════════════════════════════════ */
@media (max-width: 340px) {
  .form-header { padding: 16px 20px; }
  .form-header > * + * { margin-left: 12px; }
  .header-icon { width: 36px; height: 36px; border-radius: 10px; }
  .header-icon svg { width: 20px; height: 20px; }
  .form-title { font-size: 16px; }
  .form-sub   { font-size: 11px; }
  .header-right > * + * { margin-left: 8px; }
  .delete-btn { font-size: 12px; padding: 6px 12px; border-radius: 9px; }
  .delete-btn svg { margin-right: 5px; width: 14px; height: 14px; }
  .type-btn { font-size: 11px; padding: 5px 10px; border-radius: 7px; }
  .close-btn { width: 30px; height: 30px; border-radius: 8px; }
  .close-btn svg { width: 16px; height: 16px; }
  .col-left  { padding: 20px; }
  .col-left > * + * { margin-top: 18px; }
  .col-right { padding: 20px; }
  .col-right > * + * { margin-top: 18px; }
  .field > * + * { margin-top: 7px; }
  .field-label { font-size: 11px; }
  .field-label > * + * { margin-left: 6px; }
  .optional { font-size: 10px; }
  .extracting-badge { font-size: 10px; padding: 2px 7px; }
  .field-textarea { font-size: 13px; padding: 10px 12px; min-height: 64px; border-radius: 10px; }
  .char-count { font-size: 10px; }
  .field-hint { font-size: 10px; }
  .tags-input-wrap { padding: 7px 9px; border-radius: 10px; }
  .tags-list > * { margin: 2.5px; }
  .tag-pill { font-size: 11px; padding: 2px 8px; }
  .tag-pill > * + * { margin-left: 4px; }
  .tag-remove { font-size: 14px; }
  .tag-input  { font-size: 12px; }
  .dropzone { padding: 16px 12px; min-height: 80px; border-radius: 12px; }
  .dz-empty > * + * { margin-top: 5px; }
  .dz-icon svg { width: 26px; height: 26px; }
  .dz-label { font-size: 12px; }
  .dz-hint  { font-size: 10px; }
  .dz-filled-inner > * + * { margin-left: 10px; }
  .dz-filled-inner > svg { width: 18px; height: 18px; }
  .dz-filename { font-size: 12px; }
  .dz-meta { font-size: 10px; }
  .dz-meta > * + * { margin-left: 6px; }
  .extracted-tag { font-size: 9px; padding: 1px 5px; }
  .dz-clear { width: 26px; height: 26px; font-size: 15px; }
  .audio-native { height: 34px; border-radius: 8px; }
  .img-dropzone { height: 110px; border-radius: 12px; }
  .img-clear { width: 24px; height: 24px; font-size: 14px; top: 7px; right: 7px; }
  .color-swatch { border-radius: 9px; padding: 7px 4px 5px; }
  .color-swatch > * + * { margin-top: 4px; }
  .swatch-dot { width: 9px; height: 9px; }
  .pal-name   { font-size: 9px; }
  .form-footer { padding: 14px 20px; }
  .footer-actions > * + * { margin-left: 8px; }
  .validation-hint,
  .validation-ok { font-size: 11px; }
  .validation-hint > * + *,
  .validation-ok   > * + * { margin-left: 4px; }
  .validation-ok svg,
  .validation-hint svg { width: 13px; height: 13px; }
  .btn-secondary { font-size: 12px; padding: 8px 16px; border-radius: 9px; }
  .btn-primary   { font-size: 12px; padding: 8px 18px; border-radius: 9px; }
  .btn-primary svg { margin-right: 6px; width: 14px; height: 14px; }
}

/* ─── Transitions ──────────────────────────────────── */
.overlay-fade-enter-active, .overlay-fade-leave-active { transition: opacity 0.25s ease; }
.overlay-fade-enter-from, .overlay-fade-leave-to       { opacity: 0; }

.form-slide-enter-active { transition: opacity 0.25s ease, transform 0.28s cubic-bezier(0.34, 1.28, 0.64, 1); }
.form-slide-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.form-slide-enter-from   { opacity: 0; transform: translateY(24px) scale(0.97); }
.form-slide-leave-to     { opacity: 0; transform: translateY(12px) scale(0.98); }
</style>